import React, { type FunctionComponent, useCallback, useState, useEffect } from 'react'
import { type FormContextValues } from 'react-hook-form'
import { colors, fontSize, fontWeight, Select, spacing } from '@retailer-platform/shared-components'
import { type Styles } from 'react-select'
import styled from '@emotion/styled'
import { useShiftDetailsFormStyles } from 'domains/shifts/components/contexts/ShiftDetailsFormStylesContext'
import {
  FormRow,
  StyledDropdownContainer,
  StyledLabel,
} from 'domains/shifts/components/recurring-shifts/ShiftDetailsForm.styles'
import {
  type PartnerPickRecurringShiftsFormData,
  type RepeatShiftEnum,
  type AvailableShopperOption,
} from 'domains/shifts/components/recurring-shifts/recurringShift.types'
import { NoticeText } from 'domains/shifts/components/components/recommended-shifts/RecommendationShiftFormNotice'
import { useDomainMessages } from 'domains/shifts/utils/domain/intl'
import { useShiftsStateContext } from 'domains/shifts/components/contexts/ShiftsStateContext'

interface RecommendationDeviceDropdownProps {
  register: FormContextValues<PartnerPickRecurringShiftsFormData>['register']
  control: FormContextValues<PartnerPickRecurringShiftsFormData>['control']
  setValue: FormContextValues<PartnerPickRecurringShiftsFormData>['setValue']
  watch: FormContextValues<PartnerPickRecurringShiftsFormData>['watch']
  getValues: FormContextValues<PartnerPickRecurringShiftsFormData>['getValues']
  label: string
  date: string
  defaultValue?: RepeatShiftEnum
  warehouseLocation?: string
}

const NoticeContainer = styled.div({
  backgroundColor: colors.DETRIMENTAL.LIGHT,
  margin: `${spacing.X4} -${spacing.X16}`,
  padding: spacing.X16,
})

export const RecommendationDeviceDropdown: FunctionComponent<RecommendationDeviceDropdownProps> = ({
  setValue,
  getValues,
  label,
  warehouseLocation = '',
}) => {
  const getOptions = (): AvailableShopperOption[] => {
    const options = []
    const shoppers = getValues('shoppers') || []
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return shoppers.map(({ first_name, last_name, id }) => {
      const label = `${first_name} ${last_name} ${warehouseLocation}`
      const value = id.toString()
      options.push({ label, value })
      return { label, value }
    })
  }

  const messages = useDomainMessages({
    noAvailableDevice: 'shiftsDomain.common.noAvailableDevices',
  })

  const { setShiftsState } = useShiftsStateContext()

  const options = getOptions()
  const defaultValue = options[0]
  const [selectedShopper, setSelectedShopper] = useState<AvailableShopperOption>(defaultValue)
  const setFormValue = useCallback(
    option => {
      setValue('shopper', option, true)
    },
    [setValue]
  )

  const onRecommendationShopperChange = (shopperIdBeingEdited: number | null) => {
    setShiftsState(prevState => ({
      ...prevState,
      shopperIdBeingEdited,
    }))
  }

  useEffect(() => {
    Boolean(defaultValue) && onRecommendationShopperChange(parseInt(defaultValue.value))
    // Disabling the dependency check here as we want to only run this state update once onMount
    // eslint-disable-next-line
  }, [])  

  const handleChange = option => {
    setSelectedShopper(option)
    setFormValue(option)
    onRecommendationShopperChange(parseInt(option.value))
  }

  const styles = useShiftDetailsFormStyles()
  const overrideStyles: Styles = {
    control: styles => ({
      ...styles,
      borderColor: 'transparent',
      backgroundColor: colors.GRAYSCALE.X10,
      boxShadow: `0 0 0 1px ${colors.GRAYSCALE.X10}`,
      '&:hover': {
        backgroundColor: colors.GRAYSCALE.X10,
        boxShadow: `0 0 0 1px ${colors.GRAYSCALE.X10}`,
      },
    }),
    option: (styles, { isFocused }) => ({
      ...styles,
      backgroundColor: isFocused ? colors.GRAYSCALE.X10 : colors.GRAYSCALE.X0,
      fontWeight: isFocused ? fontWeight.BOLD : fontWeight.REGULAR,
      color: colors.GRAYSCALE.X70,
      fontSize: fontSize.X15,
      display: 'flex',
      alignItems: 'center',
      margin: `${spacing.X4} 0`,
    }),
  }

  return (
    <FormRow>
      <StyledDropdownContainer>
        <StyledLabel> {label} </StyledLabel>
        {options.length > 0 ? (
          <Select
            data-testid="shopper-shift-form-available-shopper-select"
            name="availableShopper"
            options={options}
            value={selectedShopper}
            onChange={handleChange}
            defaultValue={defaultValue}
            styles={{ ...styles, ...overrideStyles }}
          />
        ) : (
          <NoticeContainer>
            <NoticeText>{messages.noAvailableDevice}</NoticeText>
          </NoticeContainer>
        )}
      </StyledDropdownContainer>
    </FormRow>
  )
}
